import { computed } from 'vue';
import { useLiveCashQuery } from '@core/cash';
import { useDialog } from '@negar/core';
import {
  ORDER_FILTERS,
  useFilterOrders,
  useLiveOrdersQuery,
} from '@core/order';
import { InitialPublicOfferItem, OrderItem } from '@libs/api-reference';
import { IPO_ORDER_TYPE, offerHasPrice, useIPOMachine } from '@core/ipo';

import {
  BuyOfferDialog,
  DeleteOfferDialog,
  EditOfferDialog,
  OfferDetailDialog,
  AutomaticRequestDialog,
} from '@/modules/ipo';

import { i18n } from '@/plugins/i18n';

const { t } = i18n.global;

export default function useIpoDialogs() {
  const { add: addDialog } = useDialog();

  const { cashData } = useLiveCashQuery();
  const { orders } = useLiveOrdersQuery();
  const { orders: openOrders } = useFilterOrders(
    orders,
    ORDER_FILTERS.open.value
  );
  const { orders: doneOrders } = useFilterOrders(
    orders,
    ORDER_FILTERS.done.value
  );

  const getIPOStylesConfig = (offer: InitialPublicOfferItem) => {
    const isOpenOrder = computed(
      () =>
        openOrders.value.findIndex(
          (o: OrderItem) => o.instrumentIdentification === offer.instrumentIsin
        ) > -1
    );

    const isDoneOrder = computed(
      () =>
        doneOrders.value.findIndex(
          (o: OrderItem) => o.instrumentIdentification === offer.instrumentIsin
        ) > -1
    );

    return useIPOMachine({
      offer: computed(() => offer),
      buyingPower: computed(() => cashData.value.buyingPower),
      isOpenOrder,
      isDoneOrder,
    });
  };

  const openOfferDetailDialog = (offer: InitialPublicOfferItem) => {
    const ipoState = getIPOStylesConfig(offer);

    addDialog({
      title: ipoState.value.stylesConfig.showInformationInDetail
        ? t('ipo.detailTitle', { symbol: offer.instrumentTitle })
        : t('ipo.requestInfo', { symbol: offer.instrumentTitle }),
      content: computed(() => ({
        component: OfferDetailDialog,
        props: {
          offer: offer,
          state: ipoState.value.stylesConfig,
        },
      })),
    });
  };

  const openDeleteOfferDialog = ({
    orderId,
    offer,
  }: {
    offer: InitialPublicOfferItem;
    orderId?: string;
  }) => {
    const ipoState = getIPOStylesConfig(offer);

    const orderType = computed(() => {
      if (ipoState.value.stylesConfig.isPreOrder) {
        return offerHasPrice(offer)
          ? IPO_ORDER_TYPE.preOrder
          : IPO_ORDER_TYPE.preOrderNoPrice;
      }

      return IPO_ORDER_TYPE.order;
    });

    addDialog({
      title: t('ipo.cancelRequest'),
      bodyClass: '!w-[352px]',
      content: t('ipo.cancelRequestXConfirmation', {
        name: offer.instrumentTitle,
      }),
      footer: computed(() => ({
        component: DeleteOfferDialog,
        props: {
          isin: offer.instrumentIsin,
          orderType: orderType.value,
          offer,
          orderId,
        },
      })),
    });
  };

  const openEditOfferDialog = ({
    orderId,
    offer,
  }: {
    offer: InitialPublicOfferItem;
    orderId: string;
  }) => {
    const ipoState = getIPOStylesConfig(offer);

    const orderType = computed(() => {
      if (ipoState.value.stylesConfig.isPreOrder) {
        return offerHasPrice(offer)
          ? IPO_ORDER_TYPE.preOrder
          : IPO_ORDER_TYPE.preOrderNoPrice;
      }

      return IPO_ORDER_TYPE.order;
    });
    addDialog({
      title: `${t('ipo.editBuy')} ${offer.instrumentTitle}`,
      content: computed(() => ({
        component: EditOfferDialog,
        props: {
          isin: offer.instrumentIsin,
          orderId,
          orderType: orderType.value,
          offer,
        },
      })),
    });
  };

  const openBuyOfferDialog = ({ offer }: { offer: InitialPublicOfferItem }) => {
    const ipoState = getIPOStylesConfig(offer);

    const orderType = computed(() => {
      if (ipoState.value.stylesConfig.isPreOrder) {
        return offerHasPrice(offer)
          ? IPO_ORDER_TYPE.preOrder
          : IPO_ORDER_TYPE.preOrderNoPrice;
      }

      return IPO_ORDER_TYPE.order;
    });

    addDialog({
      title: `${
        orderType.value === 'order' ? t('common.buy') : t('ipo.buyRequest')
      } ${offer.instrumentTitle}`,
      content: computed(() => ({
        component: BuyOfferDialog,
        props: {
          isin: offer.instrumentIsin,
          orderType: orderType.value,
          offer,
        },
      })),
    });
  };

  const openAutomaticRequestDialog = () => {
    addDialog({
      title: t('ipo.automaticIpoRequestDialogTitle'),
      content: computed(() => ({
        component: AutomaticRequestDialog,
        props: {},
      })),
    });
  };

  return {
    openBuyOfferDialog,
    openDeleteOfferDialog,
    openEditOfferDialog,
    openOfferDetailDialog,
    openAutomaticRequestDialog,
  };
}
